/**
 * Banking chart/export helpers — SAR formatting via brandCurrency.
 */
import React from 'react';
import {
  formatBrandMoney,
  getBrandCurrencySuffix,
  getBrandDefaultCurrency,
  getBrandLocale,
  SAUDI_CURRENCY_CODE,
} from '../../shared/utils/brandCurrency';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { generateEntityReference, getEntityPrefix } from '../../shared/utils/generateEntityReference';

export { getBrandLocale };

const MONEY_EXPORT_KEYS = new Set([
  'balance', 'credit', 'debit', 'montant', 'total', 'totalAmount',
  'totalCredit', 'totalDebit', 'netFlow', 'value', 'fees', 'frais',
]);

/** Compact Y-axis tick, e.g. "1.2M ر.س" */
export function formatBankingChartTick(value) {
  const n = Number(value) || 0;
  const suffix = getBrandCurrencySuffix();
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M ${suffix}`;
  if (Math.abs(n) >= 10_000) return `${(n / 1_000).toFixed(0)}K ${suffix}`;
  return formatBrandMoney(n);
}

/** Recharts tooltip formatter — returns [formattedValue, name]. */
export function bankingChartTooltipFormatter(value, name) {
  return [formatBrandMoney(value), name || ''];
}

/** Recharts custom tooltip content props helper. */
export function BankingChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/20 bg-slate-900 px-3 py-2 text-sm shadow-xl">
      {label && <p className="mb-1 text-white/60">{label}</p>}
      {payload.map((entry) => (
        <p key={entry.dataKey} style={{ color: entry.color }} className="text-white">
          {entry.name}: {formatBrandMoney(entry.value)}
        </p>
      ))}
    </div>
  );
}

/** Chart.js tooltip label callback. */
export function bankingChartJsTooltipLabel(context) {
  const label = context.dataset?.label || '';
  const value = context.parsed?.y ?? context.parsed ?? context.raw ?? 0;
  return `${label}: ${formatBrandMoney(value)}`;
}

/** Chart.js Y-axis tick callback. */
export function bankingChartJsTick(value) {
  return formatBankingChartTick(value);
}

export function getBankingExportCurrencyCode() {
  return isSaudiBrand() ? SAUDI_CURRENCY_CODE : getBrandDefaultCurrency();
}

/** Format monetary columns for CSV/Excel exports (ar-SA + ر.س). */
export function formatBankingExportRows(rows) {
  const currency = getBankingExportCurrencyCode();
  return (rows || []).map((row) => {
    const formatted = { ...row };
    for (const [key, val] of Object.entries(formatted)) {
      if (MONEY_EXPORT_KEYS.has(key) && val != null && val !== '') {
        formatted[key] = formatBrandMoney(val);
      }
    }
    if (formatted.currency == null || formatted.currency === '') {
      formatted.currency = currency;
    }
    return formatted;
  });
}

/**
 * Modal form tab rules: ≤8 fields → no tabs | 9–17 → two tabs | ≥18 → up to three tabs.
 * Pass the full tab definition list; returns the slice to render (possibly empty).
 */
export function resolveBankingModalFormTabs(allTabs, fieldCount) {
  const count = Number(fieldCount) || 0;
  if (count <= 8) return [];
  if (count <= 17) return allTabs.slice(0, 2);
  return allTabs.slice(0, Math.min(3, allTabs.length));
}

/** Format a date string for banking reports/alerts (ar-SA). */
export function formatBankingDate(dateStr, options = {}) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleDateString(getBrandLocale(), {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  });
}

export { generateEntityReference, getEntityPrefix };

/** Map banking config prefixes into settings.configuration.entityPrefixes keys. */
export function buildBankingEntityPrefixes(config) {
  if (!config || typeof config !== 'object') return {};
  return {
    bankTransfer: config.transferReferencePrefix,
    bankCheck: config.checkReferencePrefix,
    bankStatement: config.statementReferencePrefix,
    bankAccount: config.accountReferencePrefix,
    bankEntry: config.entryReferencePrefix,
  };
}

/** Next entity reference (رمز) for banque CRUD pages. */
export function resolveBanqueEntityReference(settings, prefixKey, fallbackPrefix, records, getRef, currentValue) {
  const trimmed = String(currentValue ?? '').trim();
  if (trimmed) return trimmed;
  const prefix = getEntityPrefix(settings, prefixKey, fallbackPrefix);
  return generateEntityReference(prefix, records, getRef);
}

const ACCOUNT_CODE_PREFIX = /^CODE=([^|]+)/;

/** Parse internal account code stored in bank_accounts.notes. */
export function parseBankAccountCode(notes) {
  const match = String(notes || '').match(ACCOUNT_CODE_PREFIX);
  return match ? match[1].trim() : '';
}

/** Encode account code into notes alongside optional region/extra text. */
export function encodeBankAccountNotes(accountCode, existingNotes) {
  const extra = String(existingNotes || '').replace(ACCOUNT_CODE_PREFIX, '').replace(/^\|/, '').trim();
  const code = String(accountCode || '').trim();
  if (!code) return extra || '';
  return extra ? `CODE=${code}|${extra}` : `CODE=${code}`;
}
